import type { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import {
  GetParentSectionDocument,
  InvestorOptions,
  type Locale,
  type Stage,
  getPageWithEntities,
  type GetPagesQuery,
  type GetPagesWithEntitiesQueryVariables,
  type LinkFragment,
  type PageWhereInput,
} from '@seek/cmsu-cms-connect';
import { appendDefaultLocale } from '@seek/cmsu-cms-connect/src/helpers/localizationHelper';
import {
  TAL,
  TAL_DATA_LAB_SLUG,
  type DatalabComponent,
  type DatalabSurvey,
} from '@seek/cmsu-components/src/modules/Talent-Attraction-Lab/datalabHelper';
import type { LinkToFragment } from '@seek/cmsu-components/src/modules/types';
import type { SiteName } from '@seek/melways-sites';
import type { LoaderFunction } from 'react-router';

import { getHygraphLocales } from '../client/shared/utils/helper';
import {
  formatBlocksForRendering,
  getPreviewProps,
  getRouteInfo,
  isSlugMatchesLastSection,
  transformAwardSiteSlug,
  type PreviewProps,
} from '../helpers/loadersHelper';
import type {
  AsxAnnouncementsInvestorInformation,
  DividendInvestorInformation,
  SharePriceInvestorInformation,
} from '../helpers/pageRenderers';
import { loadDatalabData, type DatalabLoaderProps } from '../loadersTAL';
import { internalError, notFound } from '../responses';
import type { RouteProps } from '../routes';
import { json } from '../server/utils/json';
import { weblinkLoader } from '../server/weblinkLoaders';

type loadPageDataProps = {
  client: ApolloClient<NormalizedCacheObject>;
  variables: GetPagesWithEntitiesQueryVariables;
  sections: string[] | undefined;
  slug: string | undefined;
  pageNumber?: string | number;
  fullURL: string;
  site: SiteName;
};

type sectionData = {
  __typename: string;
  id: string;
  parentSection?: {
    sectionName: string;
  };
};

type sectionsQuery = {
  client: ApolloClient<NormalizedCacheObject>;
  sectionIds?: string[];
};

type LoadLinkDataProps = {
  client: ApolloClient<NormalizedCacheObject>;
  pageData: GetPagesQuery['pages'][0];
};
type LoadExternalDataProps = {
  pageData: GetPagesQuery['pages'][0];
  pageNumber?: string | number;
  request: Request;
};

export const pageLoader =
  ({ config, client, clientTAL }: RouteProps): LoaderFunction =>
  async ({ request }) => {
    // Extract variables
    const {
      country,
      hygraphSite,
      hygraphLocale,
      melwaysLocale,
      isDraft,
      routeArray,
      fullURL,
      site,
    } = config;
    const previewProps = getPreviewProps(isDraft);
    const { slug, slugWithPath, pageNo, sections } = getRouteInfo(routeArray);

    // Apply manual transformation for award site slugs to match their Hygraph formatting
    const formattedSlug = transformAwardSiteSlug(sections, slug, country);

    if (isSlugMatchesLastSection(sections, formattedSlug)) {
      throw notFound(formattedSlug);
    }

    const where = {
      OR: [{ slug: slugWithPath }, { slug: formattedSlug }],
      sites_some: { name: hygraphSite },
    };

    const variables = getPageQueryVariables(
      where,
      previewProps.stage,
      hygraphLocale,
    );

    return await loadPageData({
      client,
      variables,
      sections,
      slug: formattedSlug,
      pageNumber: pageNo,
      clientTAL,
      locale: melwaysLocale,
      request,
      fullURL,
      site,
      ...previewProps,
    });
  };

export const pageWithoutSectionLoader =
  ({ config, client, clientTAL }: RouteProps): LoaderFunction =>
  async ({ request, params }) => {
    // Extract variables
    const {
      hygraphSite,
      hygraphLocale,
      melwaysLocale,
      isDraft,
      environment,
      routeArray,
      fullURL,
      site,
    } = config;
    const { stage, queryOptions } = getPreviewProps(isDraft);
    const { slug, sections } = getRouteInfo(routeArray);

    if (isSlugMatchesLastSection(sections, slug)) {
      throw notFound(slug);
    }
    // Below condition is to cater for local testing where slug is empty
    if (!Boolean(params.slug?.trim()) && environment === 'development')
      throw notFound(params.slug);

    const where = {
      slug: params.slug,
      sites_some: { name: hygraphSite },
      relatedPageSection: null,
    };

    const variables = getPageQueryVariables(where, stage, hygraphLocale);

    return await loadPageData({
      client,
      variables,
      sections,
      slug: params.slug,
      clientTAL,
      locale: melwaysLocale,
      request,
      fullURL,
      site,
      stage,
      queryOptions,
    });
  };

const getPageQueryVariables = (
  where: PageWhereInput,
  stage: Stage,
  hygraphLocale: Locale,
): GetPagesWithEntitiesQueryVariables => {
  const locale = getHygraphLocales(hygraphLocale);
  const localesWithDefault = appendDefaultLocale(locale);
  return {
    where,
    locale,
    localesWithDefault,
    stage,
  };
};

const loadPageData = async ({
  client,
  variables,
  sections,
  slug,
  pageNumber,
  clientTAL,
  locale,
  request,
  fullURL,
  site,
  queryOptions,
}: loadPageDataProps & DatalabLoaderProps & PreviewProps) => {
  let payload;
  let datalabData;

  const isDatalabPage =
    (sections && sections.includes(TAL) && slug === TAL_DATA_LAB_SLUG) ||
    slug === TAL;

  try {
    const responses = await Promise.all([
      getPageWithEntities({
        client,
        variables,
        formatBlocksForRendering,
        site,
        queryOptions,
      }),
      isDatalabPage
        ? loadDatalabData({
            clientTAL,
            locale,
            request,
            requireSurveyResult: slug !== TAL,
            fullURL,
          })
        : undefined,
    ]);
    payload = responses[0];
    datalabData = responses[1];
  } catch (e: any) {
    if (e.code === '404') {
      throw notFound(slug);
    }
    throw internalError(e);
  }

  // Load External Data (e.g. WebLinks)
  await loadExternalData({ pageData: payload, pageNumber, request });
  await loadLinkParentSectionData({ client, pageData: payload });

  if (datalabData)
    mapDatalabSurveyDataToPageData({
      pageData: payload,
      datalabData,
    });

  return json(payload);
};

const loadExternalData = async ({
  pageData,
  pageNumber,
  request,
}: LoadExternalDataProps) => {
  for (const container of pageData.containers) {
    for (const block of container.blocks) {
      for (const item of block.items) {
        if (item.__typename === 'CCustomComponent') {
          if (item.data?.__typename === 'InvestorInformation') {
            if (item.data.options === 'Dividends') {
              item.data = {
                ...item.data,
                externalResources: await weblinkLoader.loadDividends(),
              } as DividendInvestorInformation;
            } else if (item.data.options === 'SharePrice') {
              item.data = {
                ...item.data,
                externalResources: await weblinkLoader.loadSharePrice(),
              } as SharePriceInvestorInformation;
            } else if (
              item.data.options === InvestorOptions.AsxAnnouncements ||
              item.data.options === InvestorOptions.LatestAsxAnnouncements
            ) {
              item.data = {
                ...item.data,
                externalResources:
                  await weblinkLoader.loadLatestAsxAnnouncements({
                    pageNumber,
                    request,
                  }),
              } as AsxAnnouncementsInvestorInformation;
            }
          }
        }
      }
    }
  }
};

const loadLinkParentSectionData = async ({
  client,
  pageData,
}: LoadLinkDataProps) => {
  const sectionIds = iterateContainersData(true, pageData);
  if (sectionIds) {
    const sectionList = await getParentSectionNames({ client, sectionIds });
    iterateContainersData(false, pageData, sectionList);
  }
};

const iterateContainersData = (
  fetchData: boolean,
  pageData: GetPagesQuery['pages'][0],
  sectionList?: sectionData[],
) => {
  let sectionIds;
  for (const container of pageData.containers) {
    for (const block of container.blocks) {
      for (const item of block.items) {
        if (item.__typename === 'CActionGroup') {
          for (const action of item.actions) {
            if (action.link) {
              let sectionId;
              if (action.link.to?.__typename === 'Article') {
                sectionId = action.link.to.category?.relatedCategorySection?.id;
              } else if (action.link.to?.__typename === 'Page') {
                sectionId = action.link.to.relatedPageSection?.id;
              } else if (action.link.to?.__typename === 'Category') {
                sectionId = action.link.to.relatedCategorySection?.id;
              }
              if (sectionId) {
                if (fetchData) {
                  if (!sectionIds) sectionIds = [sectionId];
                  else sectionIds.push(sectionId);
                } else {
                  const sectionData = sectionList?.find(
                    (section) => sectionId === section.id,
                  );
                  action.link = updateLinkData(action.link, sectionData);
                }
              }
            }
          }
        }
      }
    }
  }
  return sectionIds;
};

const getParentSectionNames = async ({
  client,
  sectionIds,
}: sectionsQuery): Promise<sectionData[] | undefined> => {
  const variables = {
    where: {
      id_in: sectionIds,
    },
  };

  const { data, error } = await client.query<{ sections: sectionData[] }>({
    query: GetParentSectionDocument,
    variables,
    fetchPolicy: 'network-only',
  });

  if (error) {
    throw internalError(error);
  }

  return data.sections;
};

const updateLinkData = (link: LinkFragment, sectionData?: sectionData) => {
  if (link.to && sectionData) {
    return {
      ...link,
      to: {
        ...link.to,
        parentSection: {
          sectionName: sectionData?.parentSection?.sectionName,
        },
      } as unknown as LinkToFragment,
    };
  }
};

const mapDatalabSurveyDataToPageData = async ({
  pageData,
  datalabData,
}: {
  pageData: GetPagesQuery['pages'][0];
  datalabData: DatalabSurvey;
}) => {
  let data: DatalabSurvey | undefined;
  for (const container of pageData.containers) {
    for (const block of container.blocks) {
      for (let i = 0; i < block.items.length; i++) {
        if (
          block.items[i].__typename === 'Datalab' ||
          block.items[i].__typename === 'CBanner' // For the tal-landing page for the banner custom component
        ) {
          if (!data) data = datalabData;
          block.items[i] = {
            ...block.items[i],
            datalabSurvey: data,
          } as DatalabComponent;
        }
      }
    }
  }
};
